import React, { useEffect, useState, useMemo, useCallback, lazy, Suspense, useRef } from "react";
import HomeSearch from "../components/home/HomeSearch";
import AnimeBanner from '../components/home/AnimeBanner';

import "./Home.css";

const AnimeCardsGrid = lazy(() => import("../components/AnimeCardsGrid"));
const MangaCardsGrid = lazy(() => import("../components/MangaCardsGrid"));
const CharacterCardsGrid = lazy(() => import("../components/CharacterCardsGrid"));

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Helper for conditional logging
const logDebug = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(message, ...args);
  }
};
const logError = (message, ...args) => {
  // Keep error logs even in production, but could conditionalize if needed
  console.error(message, ...args);
};

// Skeleton UI component for better loading experience
const HomePageSkeleton = () => (
  <div className="home">
    <div className="hero-section">
      <div className="skeleton-block" style={{ height: '40px', width: '80%', margin: '0 auto' }}></div>
      <div className="skeleton-block" style={{ height: '20px', width: '60%', margin: '15px auto' }}></div>
    </div>
    
    <div className="search-skeleton">
      <div className="skeleton-block" style={{ height: '50px', width: '80%', margin: '20px auto' }}></div>
    </div>
    
    {/* Section skeletons */}
    {[1, 2, 3, 4, 5].map(i => (
      <section key={i} className="media-section">
        <div className="skeleton-block" style={{ height: '24px', width: '40%', marginBottom: '15px' }}></div>
        <div className="cards-container">
          {[1, 2, 3, 4].map(j => (
            <div key={j} className="media-card skeleton">
              <div className="skeleton-image"></div>
              <div className="skeleton-block" style={{ height: '20px', width: '80%', margin: '10px 0' }}></div>
              <div className="skeleton-block" style={{ height: '16px', width: '60%' }}></div>
            </div>
          ))}
        </div>
      </section>
    ))}
  </div>
);

// Progressive loading section component with Intersection Observer
const LazySection = ({ title, items, componentType, cardLayout }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading when section is 200px from viewport
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  // Check for empty or invalid data
  if (!items || !Array.isArray(items) || items.length === 0) {
    return (
      <section className="media-section" ref={sectionRef}>
        <h2 className="section-title">
          <span>{title}</span>
        </h2>
        <div className="no-data-card">
          No {componentType} data available
          {process.env.NODE_ENV === 'development' && (
            <div style={{fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.7}}>
              Check console for errors
            </div>
          )}
        </div>
      </section>
    );
  }

  const limitedItems = items.slice(0, 8);

  return (
    <section className="media-section" ref={sectionRef}>
      <h2 className="section-title">
        <span>{title}</span>
      </h2>
      
      {shouldLoad ? (
        <Suspense fallback={
          <div className="cards-container skeleton-container">
            {[1, 2, 3, 4].map(j => (
              <div key={j} className="media-card skeleton">
                <div className="skeleton-image"></div>
                <div className="skeleton-block" style={{ height: '20px', width: '80%', margin: '10px 0' }}></div>
              </div>
            ))}
          </div>
        }>
          {componentType === "manga" ? (
            <MangaCardsGrid mangas={limitedItems} layout={cardLayout} />
          ) : componentType === "character" ? (
            <CharacterCardsGrid characters={limitedItems} layout={cardLayout} />
          ) : (
            <AnimeCardsGrid animes={limitedItems} layout={cardLayout} />
          )}
        </Suspense>
      ) : (
        <div className="cards-container skeleton-container">
          {[1, 2, 3, 4].map(j => (
            <div key={j} className="media-card skeleton">
              <div className="skeleton-image"></div>
              <div className="skeleton-block" style={{ height: '20px', width: '80%', margin: '10px 0' }}></div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const Home = () => {
  const [mediaData, setMediaData] = useState({
    topRatedAnime: [],
    currentlyAiringAnime: [],
    topRatedManga: [],
    currentlyPublishingManga: [],
    topCharacters: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [cardLayout, setCardLayout] = useState("horizontal");
  const [showDebug, setShowDebug] = useState(false);
  
  // Extract data for readability
  const { topRatedAnime, currentlyAiringAnime, topRatedManga, currentlyPublishingManga, topCharacters } = mediaData;
  
  // Use useCallback for debug toggle to maintain referential equality
  const toggleDebug = useCallback(() => {
    setShowDebug(prev => !prev);
  }, []);

  // Update layout based on screen size with useCallback
  const handleResize = useCallback(() => {
    // Switch to vertical layout on smaller screens
    setCardLayout(window.innerWidth <= 768 ? "vertical" : "horizontal");
  }, []);
  
  // Effect for window resize
  useEffect(() => {
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Helper function to handle both paginated and direct array responses
  const extractData = useCallback((responseData, endpointName) => {
    // If it's an array, it's using the old direct format
    if (Array.isArray(responseData)) {
      logDebug(`${endpointName} returned direct array format with ${responseData.length} items`);
      return responseData;
    }
    
    // If it has a data property that's an array, it's using the new paginated format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      logDebug(`${endpointName} returned paginated format with ${responseData.data.length} items`);
      return responseData.data;
    }
    
    // If it's neither, log an error and return empty array
    logError(`Unexpected data format for ${endpointName}:`, responseData);
    return [];
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchFreshData();
      } catch (error) {
        logError("Error in fetch process:", error);
        setFetchError("Failed to load homepage data. Please try again.");
      }
    };
    
    fetchData();
  }, []);
  
  // Separate function for the actual API fetching with retry logic
  const fetchFreshData = async (retries = 3) => {
    try {
      // Reset any previous error
      setFetchError(null);
      
      // Single fetch request
      const response = await fetch(`${API_BASE_URL}/api/homepage`);
      
      if (!response.ok) {
        if (response.status === 429 && retries > 0) {
          // Rate limited, retry with exponential backoff
          const delay = Math.pow(2, 3 - retries) * 1000;
          logDebug(`Rate limited, retrying in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchFreshData(retries - 1);
        }
        
        throw new Error(`Error fetching homepage data: ${response.status}`);
      }
      
      const homepageData = await response.json();
      
      const processedData = {
        topRatedAnime: homepageData.topRatedAnime || [],
        currentlyAiringAnime: homepageData.currentlyAiringAnime || [],
        topRatedManga: homepageData.topRatedManga || [],
        currentlyPublishingManga: homepageData.currentlyPublishingManga || [],
        topCharacters: homepageData.topCharacters || []
      };
      
      // Update state
      setMediaData(processedData);
      setIsLoading(false);
      
      // Cache the data
      localStorage.setItem('homepageData', JSON.stringify(processedData));
      localStorage.setItem('homepageTimestamp', Date.now().toString());
      
      return processedData;
      
    } catch (error) {
      logError("Error fetching fresh data:", error);
      setFetchError(error.message);
      setIsLoading(false);
      return null;
    }
  };

  // Memoize debug section
  const debugSection = useMemo(() => {
    if (!showDebug || process.env.NODE_ENV !== 'development') return null;
    
    return (
      <div style={{
        margin: '1rem',
        padding: '1rem',
        background: 'rgba(0,0,0,0.8)',
        borderRadius: '8px',
        color: '#eee',
        textAlign: 'left',
        fontFamily: 'monospace',
        fontSize: '0.8rem',
        maxHeight: '400px',
        overflow: 'auto'
      }}>
        <h3>Debug Information</h3>
        <p>Anime Items: {topRatedAnime.length}</p>
        <p>Manga Items: {topRatedManga.length}</p>
        <p>Character Items: {topCharacters.length}</p>
        
        <h4>First Anime Item</h4>
        <pre>{JSON.stringify(topRatedAnime[0], null, 2)}</pre>
        
        <h4>First Manga Item</h4>
        <pre>{JSON.stringify(topRatedManga[0], null, 2)}</pre>
        
        <h4>First Character Item</h4>
        <pre>{JSON.stringify(topCharacters[0], null, 2)}</pre>
      </div>
    );
  }, [showDebug, topRatedAnime, topRatedManga, topCharacters]);

  // Loading state with skeleton UI
  if (isLoading) {
    return <HomePageSkeleton />;
  }

  // Error state
  if (fetchError) {
    return (
      <div className="error-container">
        <h3>Oops! Something went wrong</h3>
        <p>{fetchError}</p>
        <button onClick={() => fetchFreshData()} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <main className="home">
      <AnimeBanner />
      <div className="hero-section">
        <h2 className="hero-heading" data-text="Your Gateway to Anime & Manga">
          Your Gateway to Anime & Manga</h2>
        <p className="hero-text">
          Discover, track, and explore your favorite anime and manga in one
          modern platform
        </p>
      </div>

      <HomeSearch />

      {/* Content Sections with lazy loading */}
      <LazySection 
        title="Top-Rated Anime" 
        items={topRatedAnime} 
        componentType="anime" 
        cardLayout={cardLayout} 
      />
      <LazySection 
        title="Currently Airing Anime" 
        items={currentlyAiringAnime} 
        componentType="anime" 
        cardLayout={cardLayout} 
      />
      <LazySection 
        title="Top-Rated Manga" 
        items={topRatedManga} 
        componentType="manga" 
        cardLayout={cardLayout} 
      />
      <LazySection 
        title="Currently Publishing Manga" 
        items={currentlyPublishingManga} 
        componentType="manga" 
        cardLayout={cardLayout} 
      />
      <LazySection 
        title="Popular Characters" 
        items={topCharacters} 
        componentType="character" 
        cardLayout={cardLayout} 
      />

      {/* Debug Button - only visible in development */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button 
            onClick={toggleDebug}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
          </button>
          
          {debugSection}
        </div>
      )}
    </main>
  );
};

export default React.memo(Home);